import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import { useEffect, useState } from 'react';
import Button from '@hellodarwin/core/lib/components/common/button';
import FormLabel from '@hellodarwin/core/lib/components/common/form-label';
import InputNumber from '@hellodarwin/core/lib/components/common/input-number';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import UploadIcon from '@hellodarwin/icons/dist/icons/Upload';
import { Form, Input, Modal, Upload } from 'antd';
import { useTheme } from 'styled-components';
import GinStepUploadFormRelatedFiles from './gin-step-upload-form-related-files';
import SelectedAssetCard from './selected-asset-card';
import { ApplicationAssetActions, ApplicationAssetFields, AssetDropZonePrompt, ApplicationUploadSection, FormButtonRow, YearModalFields, } from './styles';
const { Dragger } = Upload;
const GinStepApplicationAssetUploadForm = ({ handleSaveAsset, asset, handleAfterFinish, handleModifyAsset, isDrawerOpen, isLoading, companyName, grantName, ginAssetType, ginCustomAssetType, fetchRelatedAssets, relatedFilesTitle, flattenRelatedFiles, }) => {
    const theme = useTheme();
    const { t } = useTranslations();
    const isEditMode = !!asset;
    const currentYear = new Date().getFullYear();
    const [form] = Form.useForm();
    const [newAsset, setNewAsset] = useState(undefined);
    const [copyFile, setCopyFile] = useState(undefined);
    const [year, setYear] = useState(currentYear);
    const [yearModalVisible, setYearModalVisible] = useState(false);
    const [tempYear, setTempYear] = useState(currentYear);
    const fileName = (newAsset === null || newAsset === void 0 ? void 0 : newAsset.name) ||
        (copyFile === null || copyFile === void 0 ? void 0 : copyFile.file_name) ||
        (copyFile === null || copyFile === void 0 ? void 0 : copyFile.display_name) ||
        (asset === null || asset === void 0 ? void 0 : asset.file_name) ||
        (asset === null || asset === void 0 ? void 0 : asset.display_name);
    const translatedAssetType = ginAssetType === 'other'
        ? ginCustomAssetType || ginAssetType
        : t(`gin_steps|asset_type_${ginAssetType}`);
    const generateDisplayName = (yearToUse) => `${companyName} | ${grantName || 'grant'} | ${translatedAssetType} | ${yearToUse}`;
    const fileNamePreview = isEditMode || !newAsset ? null : generateDisplayName(year);
    const selectedDisplayName = fileNamePreview || (copyFile === null || copyFile === void 0 ? void 0 : copyFile.display_name) || (asset === null || asset === void 0 ? void 0 : asset.display_name) || fileName;
    const selectedOriginalName = (newAsset === null || newAsset === void 0 ? void 0 : newAsset.name) ||
        (copyFile === null || copyFile === void 0 ? void 0 : copyFile.file_name) ||
        (copyFile === null || copyFile === void 0 ? void 0 : copyFile.display_name) ||
        (asset === null || asset === void 0 ? void 0 : asset.file_name) ||
        (asset === null || asset === void 0 ? void 0 : asset.display_name);
    useEffect(() => {
        if (!isDrawerOpen) {
            setNewAsset(undefined);
            setCopyFile(undefined);
            setYear(currentYear);
            setTempYear(currentYear);
            setYearModalVisible(false);
        }
        form.resetFields();
    }, [isDrawerOpen, form, currentYear]);
    useEffect(() => {
        if (isEditMode && (asset === null || asset === void 0 ? void 0 : asset.display_name)) {
            form.setFieldsValue({ display_name: asset.display_name });
        }
    }, [asset]);
    const afterFinish = () => {
        form.resetFields();
        setNewAsset(undefined);
        setCopyFile(undefined);
        setYear(currentYear);
        setTempYear(currentYear);
        setYearModalVisible(false);
        handleAfterFinish();
    };
    const handleSubmit = async () => {
        try {
            await form.validateFields();
            if (!newAsset && !copyFile) {
                antdMessage.error('No file selected for upload. Please select a file.');
                return;
            }
            const displayName = newAsset
                ? generateDisplayName(year)
                : form.getFieldValue('display_name') || '';
            await handleSaveAsset({
                newFile: newAsset,
                display_name: displayName,
                copyFile,
            });
            afterFinish();
        }
        catch (info) {
            console.error('Form validation failed:', info);
        }
    };
    const beforeUpload = (assetFile) => {
        setNewAsset(assetFile);
        setCopyFile(undefined);
        return false;
    };
    const onRemove = () => {
        setNewAsset(undefined);
        setCopyFile(undefined);
    };
    const handleRename = async () => {
        try {
            const values = await form.validateFields();
            const displayName = values.display_name;
            if (!displayName) {
                antdMessage.error('Please enter a name');
                return;
            }
            await (handleModifyAsset === null || handleModifyAsset === void 0 ? void 0 : handleModifyAsset(displayName));
            afterFinish();
        }
        catch (info) {
            console.error('Form validation failed:', info);
            antdMessage.error('Please enter a name');
        }
    };
    const openYearModal = () => {
        setTempYear(year);
        setYearModalVisible(true);
    };
    const handleYearModalOk = () => {
        setYear(tempYear);
        setYearModalVisible(false);
    };
    const handleYearModalCancel = () => {
        setYearModalVisible(false);
    };
    const handleCopyFile = (file) => {
        setCopyFile(file);
        setNewAsset(undefined);
        form.setFieldsValue({
            display_name: file.display_name,
        });
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Form, { layout: "vertical", form: form, children: [_jsxs(ApplicationAssetFields, { children: [(isEditMode || copyFile) && (_jsx(Form.Item, { name: "display_name", label: t('assetsUpload|file_name'), rules: [
                                    {
                                        required: true,
                                        message: t('assetsUpload|file_name_is_required'),
                                    },
                                ], style: { width: '100%' }, children: _jsx(Input, { disabled: isLoading }) })), _jsxs(Form.Item, { required: true, name: 'asset', label: t('assetsUpload|file_asset'), style: { width: '100%' }, children: [fileName && selectedDisplayName && (_jsx(SelectedAssetCard, { displayName: selectedDisplayName, originalName: selectedOriginalName, disabled: isLoading, onEdit: newAsset ? openYearModal : undefined, onRemove: newAsset || copyFile ? onRemove : undefined })), !isEditMode && !fileName && (_jsx(Dragger, { name: "file", beforeUpload: beforeUpload, onRemove: onRemove, fileList: [], pastable: true, disabled: isLoading, children: _jsxs(AssetDropZonePrompt, { children: [_jsx(UploadIcon, {}), _jsx(Typography, { elementTheme: "body2", children: t('assetsUpload|file_asset_drag_and_drop') })] }) }))] })] }), _jsx(ApplicationAssetActions, { children: _jsxs(FormButtonRow, { children: [_jsx(Button, { size: "small", defaultStyle: theme.colors.white_1, disabled: isLoading, onClick: afterFinish, children: t('button|cancel') }), !isEditMode ? (_jsx(Button, { size: "small", defaultStyle: theme.colors.primary, loading: isLoading, disabled: isLoading, onClick: handleSubmit, children: t('button|save') })) : isEditMode ? (_jsx(Button, { size: "small", defaultStyle: theme.colors.primary, loading: isLoading, disabled: isLoading, onClick: handleRename, children: t('button|update') })) : null] }) })] }), !isEditMode ? (_jsx(ApplicationUploadSection, { "$flat": flattenRelatedFiles, children: _jsx(GinStepUploadFormRelatedFiles, { assets: copyFile ? [copyFile] : undefined, handleCopyFile: handleCopyFile, fetchRelatedAssets: fetchRelatedAssets, title: relatedFilesTitle, flat: flattenRelatedFiles }) })) : null, _jsx(Modal, { title: t('common|fileActions.editName'), open: yearModalVisible, onOk: handleYearModalOk, onCancel: handleYearModalCancel, width: 300, okText: t('button|save'), cancelText: t('button|cancel'), closable: !isLoading, maskClosable: !isLoading, okButtonProps: { disabled: isLoading, loading: isLoading }, cancelButtonProps: { disabled: isLoading }, children: _jsxs(YearModalFields, { children: [_jsx(FormLabel, { label: t('assetsUpload|year') }), _jsx(InputNumber, { style: { width: '100%' }, value: tempYear, onChange: (value) => setTempYear(value || currentYear), placeholder: t('assets|year'), disabled: isLoading })] }) })] }));
};
export default GinStepApplicationAssetUploadForm;
